import React, { useEffect, useState } from "react";
import "./account.css";
import fieldStyles from "./account.module.css";
import Box from "@mui/material/Box";
import { Grid, Button, Chip, DialogContent, Typography } from "@material-ui/core";
import Avatar from "@mui/material/Avatar";
import { Formik, Form, Field } from "formik";
import { TextField } from "formik-material-ui";
import * as Yup from "yup";
import { makeStyles } from "@material-ui/core/styles";
import useAxiosPrivate from "../../utils/hooks/useAxiosPrivate";
import { useDispatch, useSelector } from "react-redux";
import { setName, setProfile, setEmail } from "../../store/actions/authActions";
import DropFileInput from "./dropImage";
import AccountModalAdmin from "./accountModalAdmin";
import ModalCloseButton from "../../components/UI/ModalCloseButton";
import Alert from "../../components/UI/alert";
import Dialog from "@mui/material/Dialog";
import { REQUESTER_ROLES } from "../../utils/constants/roles";
import {
  modalBackdropProps,
  MODAL_PAPER_SX,
} from "../../utils/styles/modal.overlay";

const useStyles = makeStyles((theme) => ({
  chip: {
    fontFamily: "Poppins",
    backgroundColor: "var(--primary-popup-background-color)",
    border: "2px solid",
    borderColor: "var(--primary-text-theme-color)",
    fontWeight: "400",
    color: "var(--primary-text-color)",
    fontSize: "0.8rem",
    margin: "0.4rem",
    padding: "0.5rem 0.5rem 0.5rem 0.5rem",
    lineHeight: "1rem",
  },
}));

let AccountSchema = Yup.object().shape({
  first_name: Yup.string()
    .min(2, "First name must be at least 2 characters")
    .max(15, "First name must be at most 15 characters")
    .required("First Name is required!"),
  last_name: Yup.string()
    .min(2, "Last name must be at least 2 characters")
    .max(15, "Last name must be at most 15 characters")
    .required("Last Name is required!"),
});

export default function AccountModal(props) {
  const axios = useAxiosPrivate();
  const role = useSelector((state) => state.auth.role);
  const classes = useStyles();
  const { setOpen, open, setOpenedPassword, setOpenAuthentication } = props;
  const [edit, setEdit] = useState(false);
  const [initialValues, setInitialValues] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    url: "",
  });
  const [teams, setTeams] = useState([]);
  const [alert, setAlert] = useState({
    showAlert: false,
    severity: "success",
    message: "",
  });
  const dispatch = useDispatch();

  useEffect(() => {
    loadData();
    // eslint-disable-next-line
  }, [open]);

  const onFileChange = async (files) => {
    loadData();
  };

  const loadData = async (e) => {
    try {
      const { data } = await axios.get("/user/profile");
      const imageUrl = data?.data?.avatar_url;
      setInitialValues({
        first_name: data?.data?.first_name,
        last_name: data?.data?.last_name,
        email: data?.data?.email,
        password: "hiddenPassword",
        url: imageUrl,
      });
      dispatch(setEmail(data?.data?.email));
      dispatch(setProfile(imageUrl));
      setTeams(data?.data?.groups || []);
      setEdit(true);
    } catch (error) {
      console.log(error);
      if (error.response && error.response?.data) {
        setAlert({
          showAlert: true,
          severity: "error",
          message: error.response?.data?.message,
        });
      } else {
        setAlert({
          showAlert: true,
          severity: "error",
          message: "Something was wrong!",
        });
      }
    }
  };

  const submit = async (e) => {
    try {
      await axios.post("/user/update", {
        first_name: e.first_name,
        last_name: e.last_name,
      });
      dispatch(setName(e.first_name));
      setEdit(true);
      setAlert({
        showAlert: true,
        severity: "success",
        message: "User updated Successfully!",
      });
      setTimeout(() => {
        setAlert({
          showAlert: false,
          severity: "success",
          message: "User updated Successfully!",
        });
      }, 5000);
    } catch (error) {
      if (error.response && error.response?.data) {
        setAlert({
          showAlert: true,
          severity: "error",
          message: error.response?.data?.message,
        });
      } else {
        setAlert({
          showAlert: true,
          severity: "error",
          message: "Something was wrong!",
        });
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      BackdropProps={modalBackdropProps}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: MODAL_PAPER_SX,
      }}
    >
      <DialogContent className={fieldStyles.accountDialogContent}>
        <Formik
          initialValues={initialValues}
          onSubmit={submit}
          validationSchema={AccountSchema}
        >
          {({ dirty, isValid }) => {
            return (
              <Form>
                <Grid container>
                  <Grid item xs={12} sm={12} md={12}>
                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                      <ModalCloseButton
                        onClick={() => setOpen(false)}
                        style={{ position: "static" }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography className={fieldStyles.accountTitle}>
                      Account
                    </Typography>
                    <div className={fieldStyles.avatarSection}>
                      <div className={fieldStyles.avatarEditWrap}>
                        <Avatar
                          alt="user profile picture"
                          src={initialValues.url}
                          sx={{ width: 90, height: 90 }}
                        />
                        <DropFileInput onFileChange={(files) => onFileChange(files)} />
                      </div>
                    </div>
                  </Grid>
                  <Grid item xs={12}>
                    <div className={fieldStyles.nameFieldsRow}>
                      <div className={fieldStyles.nameFieldStack}>
                        <label
                          className={fieldStyles.nameFieldLabel}
                          htmlFor="account-first-name"
                        >
                          First Name
                        </label>
                        <Field
                          id="account-first-name"
                          style={{ fontFamily: "Poppins" }}
                          name="first_name"
                          label="First Name"
                          disabled={edit}
                          component={TextField}
                          variant="outlined"
                          fullWidth
                          margin="none"
                          className={fieldStyles.accountTextField}
                          InputLabelProps={{
                            classes: {
                              root: `${fieldStyles.accountFieldLabel} ${fieldStyles.hiddenFieldLabel}`,
                            },
                          }}
                          InputProps={{
                            classes: {
                              root: `${fieldStyles.loginFieldInputRoot} ${fieldStyles.accountInputRoot}`,
                            },
                            notched: false,
                          }}
                        />
                      </div>
                      <div className={fieldStyles.nameFieldStack}>
                        <label
                          className={fieldStyles.nameFieldLabel}
                          htmlFor="account-last-name"
                        >
                          Last Name
                        </label>
                        <Field
                          id="account-last-name"
                          style={{ fontFamily: "Poppins" }}
                          name="last_name"
                          label="Last Name"
                          disabled={edit}
                          component={TextField}
                          variant="outlined"
                          fullWidth
                          margin="none"
                          className={fieldStyles.accountTextField}
                          InputLabelProps={{
                            classes: {
                              root: `${fieldStyles.accountFieldLabel} ${fieldStyles.hiddenFieldLabel}`,
                            },
                          }}
                          InputProps={{
                            classes: {
                              root: `${fieldStyles.loginFieldInputRoot} ${fieldStyles.accountInputRoot}`,
                            },
                            notched: false,
                          }}
                        />
                      </div>
                      <div className={fieldStyles.nameFieldAction}>
                        {!edit &&
                          (isValid ? (
                            <Button
                              type="submit"
                              disableRipple
                              disableFocusRipple
                              className={`${fieldStyles.nameActionButton} ${fieldStyles.saveButton}`}
                            >
                              Save
                            </Button>
                          ) : (
                            <Button
                              type="button"
                              disabled
                              disableRipple
                              disableFocusRipple
                              className={`${fieldStyles.nameActionButton} ${fieldStyles.saveButtonDisabled}`}
                            >
                              Save
                            </Button>
                          ))}
                        {edit && (
                          <Button
                            type="button"
                            disableRipple
                            disableFocusRipple
                            onClick={() => setEdit(false)}
                            className={`${fieldStyles.nameActionButton} ${fieldStyles.editButton}`}
                          >
                            Edit
                          </Button>
                        )}
                      </div>
                    </div>
                  </Grid>
                  <Grid item xs={12}>
                    <div className={fieldStyles.fieldBlock}>
                      <div className={fieldStyles.fieldHeader}>
                        <span className={fieldStyles.fieldHeaderLabel}>Email</span>
                        <span
                          className={fieldStyles.fieldHeaderLink}
                          onClick={() => setOpenAuthentication(true)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              setOpenAuthentication(true);
                            }
                          }}
                          role="button"
                          tabIndex={0}
                        >
                          {REQUESTER_ROLES.includes(role) ? "Request to change" : "Change"}
                        </span>
                      </div>
                      <Field
                        style={{ fontFamily: "Poppins" }}
                        name="email"
                        label="Email"
                        disabled
                        component={TextField}
                        variant="outlined"
                        fullWidth
                        margin="none"
                        className={fieldStyles.accountTextField}
                        InputLabelProps={{
                          classes: {
                            root: `${fieldStyles.accountFieldLabel} ${fieldStyles.hiddenFieldLabel}`,
                          },
                        }}
                        InputProps={{
                          classes: {
                            root: `${fieldStyles.loginFieldInputRoot} ${fieldStyles.accountInputRoot}`,
                          },
                          notched: false,
                        }}
                      />
                    </div>
                  </Grid>
                  <Grid item xs={12}>
                    <div className={fieldStyles.fieldBlock}>
                      <div className={fieldStyles.fieldHeader}>
                        <span className={fieldStyles.fieldHeaderLabel}>Password</span>
                        <span
                          className={fieldStyles.fieldHeaderLink}
                          onClick={() => setOpenedPassword(true)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              setOpenedPassword(true);
                            }
                          }}
                          role="button"
                          tabIndex={0}
                        >
                          Change
                        </span>
                      </div>
                      <Field
                        name="password"
                        label="Password"
                        component={TextField}
                        variant="outlined"
                        fullWidth
                        margin="none"
                        disabled
                        type="password"
                        style={{ fontFamily: "Poppins" }}
                        className={fieldStyles.accountTextField}
                        InputLabelProps={{
                          classes: {
                            root: `${fieldStyles.accountFieldLabel} ${fieldStyles.hiddenFieldLabel}`,
                          },
                        }}
                        InputProps={{
                          classes: {
                            root: `${fieldStyles.loginFieldInputRoot} ${fieldStyles.accountInputRoot}`,
                          },
                          notched: false,
                        }}
                      />
                    </div>
                  </Grid>
                  {role === "COACH" ? (
                    teams.length > 0 ? (
                      <Grid item xs={12}>
                        <div className={fieldStyles.teamsSection}>
                          <Typography className={fieldStyles.teamsTitle}>
                            Teams
                          </Typography>
                          <Box className={fieldStyles.teamsList}>
                            {teams.map((item) => (
                              <Chip
                                key={item.id || item.name}
                                label={item.name}
                                className={classes.chip}
                                size="small"
                              />
                            ))}
                          </Box>
                        </div>
                      </Grid>
                    ) : null
                  ) : (
                    <AccountModalAdmin />
                  )}
                </Grid>
                <Grid item xs={12} sm={12} md={12}>
                  <Alert
                    showAlert={alert.showAlert}
                    severity={alert.severity}
                    message={alert.message}
                    setAlert={setAlert}
                  />
                </Grid>
              </Form>
            );
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}
